import type { MarketingPageContent } from '@/config/marketing-content/types'
import {
  marketingHeroSrc,
  marketingShowcaseSrc,
  portfolioIntelligenceBasketsSrc,
} from '@/config/marketing-assets.config'
import { getAppUrl } from '@/config/marketing-nav.config'

const signupHref = getAppUrl('/signup')

const sharedFeatureCta = {
  ctaTitle: 'The future of trading feels human again',
  ctaLead: 'Stop trading through disconnected tools and build your own financial Jarvis.',
  ctaPrimaryLabel: 'Get Started',
  ctaPrimaryHref: signupHref,
} as const

function withShowcase(content: Omit<MarketingPageContent, 'showcaseImage'>): MarketingPageContent {
  return { ...content, showcaseImage: marketingShowcaseSrc(content.slug) }
}

export const portfolioIntelligenceContent: MarketingPageContent = withShowcase({
  slug: 'portfolio-intelligence',
  eyebrow: 'Portfolio Intelligence',
  heroTitle: 'Every account, one source of truth',
  heroLead:
    'Ivy brings your brokerage accounts, positions, options exposure, watchlists, and risk profile into one portfolio-aware workspace - so every answer starts with what you actually own.',
  heroImage: marketingHeroSrc('portfolio-intelligence'),
  showcaseTitle: 'Bring your portfolio into the conversation',
  showcaseLead:
    'Ivy connects to your brokerage accounts to tailor answers to your holdings, risk, and market exposure.',
  showcasePills: [
    { label: 'Multi-account awareness' },
    { label: 'Multi-account calibration' },
    { label: 'Live positions and options exposure' },
    { label: 'Allocation and concentration analysis' },
    { label: 'Watchlists connected to market context' },
    { label: 'Position-aware insights' },
  ],
  howItWorksEyebrow: 'How Portfolio Intelligence Works',
  howItWorksTitle: 'Your brokerage isn’t an afterthought',
  howItWorksLead:
    'Connect once, and every Ivy answer is shaped by what you actually hold across every account.',
  steps: [
    {
      title: 'Multi-account net worth',
      description:
        'Pull positions, cash, and options exposure from every connected brokerage into a single live view.',
    },
    {
      title: 'Risk profile calibration',
      description:
        'Tune Ivy per account so answers match your real risk tolerance, time horizon, and trading style.',
    },
    {
      title: 'Position-aware prompts',
      description:
        'Ivy suggests relevant questions based on your portfolio, risk, hedges, and upcoming events.',
    },
  ],
  featureShowcases: [
    {
      kicker: 'Ivy Baskets',
      title: 'Not ready to connect a brokerage? Start with a basket.',
      lead:
        'Every Portfolio Intelligence workspace includes Ivy Baskets — simulated portfolios you build from a thesis, a ticker list, or a portfolio you copy in. Full P&L, cost basis, and allocation tracking, live, with no brokerage connection required.',
      imageSrc: portfolioIntelligenceBasketsSrc(),
      imageAlt: 'Ivy Baskets simulated portfolios in the Portfolio Intelligence workspace',
      ctaLabel: 'Get Started',
      ctaHref: signupHref,
    },
  ],
  faqEyebrow: 'Portfolio Intelligence FAQ',
  faqTitle: 'Common questions about your portfolio',
  faqLead: 'Everything traders ask before connecting a brokerage.',
  faqItems: [
    {
      question: 'Which brokerages does Ivy support?',
      answer: 'Ivy supports most of the major US brokerages.',
    },
    {
      question: 'Is my brokerage data secure?',
      answer:
        'Yes. Connections use encrypted, read-only OAuth through our third-party brokerage connection service. Ivy can view positions and balances to personalize answers. It cannot move money or place trades.',
    },
    {
      question: 'Can Ivy place trades on my behalf?',
      answer: 'No. Ivy at the moment is read-only. You execute every trade through your brokerage.',
    },
    {
      question: 'How often does my portfolio refresh?',
      answer:
        'Positions, cash, and options exposure sync automatically throughout the day whenever your broker reports an update.',
    },
    {
      question: 'Does Ivy control my brokerage account?',
      answer: 'No. Ivy has read-only access. You stay in full control of your accounts.',
    },
    {
      question: 'What’s risk profile calibration?',
      answer:
        'Tune Ivy per account so answers match your real risk tolerance, time horizon, and trading style.',
    },
    {
      question: 'Do I need to connect a brokerage to use Ivy Baskets?',
      answer:
        "No. Ivy Baskets are simulated portfolios that work the moment you sign up — no linked account required. Connect a brokerage later, whenever you're ready, and your baskets stay right where they are alongside your real holdings.",
    },
  ],
  ...sharedFeatureCta,
})

export const marketIntelligenceContent: MarketingPageContent = withShowcase({
  slug: 'market-intelligence',
  eyebrow: 'Market Intelligence',
  heroTitle: 'From noise to signal, on demand',
  heroLead:
    'Live prices, options, and scans resolve into portfolio-relevant answers the instant you ask — the market, finally legible.',
  heroImage: marketingHeroSrc('market-intelligence'),
  showcaseTitle: 'Turn market noise into portfolio signals',
  showcaseLead:
    'Ask Ivy anything and get live data, portfolio answers, charts, scans, options, and watchlist signals in one thread.',
  showcasePills: [
    { label: 'Real-time market context and charting' },
    { label: 'Real-time options & Greeks' },
    { label: 'Equity scans and live setup discovery' },
    { label: 'One-click company Snapshots' },
    { label: 'Persistent watchlists and ticker workflows' },
    { label: 'Ask Ivy directly from any ticker' },
  ],
  howItWorksEyebrow: 'How Market Intelligence Works',
  howItWorksTitle: 'Live markets, in your conversation',
  howItWorksLead:
    'Charts, scans, and options - framed for the portfolio you actually have.',
  steps: [
    {
      title: 'Real-time charts & quotes',
      description:
        'Live prices, intraday charts, and historical context render inline - without switching apps.',
    },
    {
      title: 'Intraday price action scanner',
      description:
        'Vol spikes, gap-ups, 52-week highs, options flow, and more - with directional filtering layered on top.',
    },
    {
      title: 'Fundamentals & filings, inline',
      description:
        'SEC filings, earnings transcripts, and fundamental data surface alongside live prices and options - no extra tab, no extra tool.',
    },
  ],
  faqEyebrow: 'Market Intelligence FAQ',
  faqTitle: 'Common questions about live markets',
  faqLead: 'What traders ask about Ivy’s market-data layer.',
  faqItems: [
    {
      question: 'How real-time is the market data?',
      answer:
        'Equity and options prices are real-time during market hours. Off hours, you’ll see the last print plus after-hours indication.',
    },
    {
      question: 'What scans does Ivy run?',
      answer:
        'Vol spikes, gap ups, 52-week highs, and more – with directional filtering layered on top. But you can ask Ivy to do other scans on your behalf.',
    },
    {
      question: 'Can I save scans as a watchlist?',
      answer:
        'Yes. Ask Ivy to save any scan result as a watchlist and pick it back up in charts, scans, or conversations.',
    },
    {
      question: 'Does Ivy cover option Greeks?',
      answer:
        'Yes. Option chains include Greeks and exposure alongside live prices in your portfolio context.',
    },
    {
      question: 'Can Ivy read SEC filings?',
      answer:
        'Yes. Ivy has access to SEC filings, earnings call transcripts, and other fundamental data.',
    },
    {
      question: 'Can I chart inside the chat?',
      answer:
        'Live prices, intraday charts, and historical context render without switching apps.',
    },
  ],
  ...sharedFeatureCta,
})

export const researchIntelligenceContent: MarketingPageContent = withShowcase({
  slug: 'research-intelligence',
  eyebrow: 'Research Intelligence',
  heroTitle: 'An institutional research desk, on call',
  heroLead:
    'Specialized agents run earnings, valuation, sentiment, and strategy workflows in parallel - orchestrated into one dossier no single analyst could assemble alone.',
  heroImage: marketingHeroSrc('research-intelligence'),
  showcaseTitle: 'Your hyper-specialized research desk',
  showcaseLead:
    'Ivy Pro gives active traders specialized AI agents for deeper, structured market research.',
  showcasePills: [
    {
      label: 'Earnings Analyst',
      description:
        'Analyze earnings results, guidance, call commentary, surprises, and the market reaction.',
    },
    {
      label: 'Fundamental Valuation',
      description:
        'Review valuation drivers, financial context, comparable positioning, and long-term business quality.',
    },
    {
      label: 'Sentiment & Narrative',
      description:
        'Track the market story around a company, including news, narrative shifts, and investor attention.',
    },
    {
      label: 'Risk Monitor',
      description:
        'Evaluate risk signals, red flags, and areas where the market may be overlooking downside.',
    },
    {
      label: 'Competitive & Strategy',
      description:
        'Understand a company’s competitive position, strategic direction, and industry context.',
    },
  ],
  howItWorksEyebrow: 'How Research Intelligence Works',
  howItWorksTitle: 'Research agents that do the work',
  howItWorksLead:
    'Five specialized research agents that turn open-ended chat into structured, sourced output.',
  steps: [
    {
      title: 'Specialized, not generic',
      description:
        'Each agent runs a focused workflow - earnings, valuation, sentiment - instead of one model trying to do everything.',
    },
    {
      title: 'Structured outputs',
      description:
        'Agents return scored sections (recommendation, signals, risks) so you can scan a thesis in seconds, not minutes.',
    },
    {
      title: 'One-click full dossier',
      description:
        'Run the full suite on any ticker and get a consolidated research package - earnings, valuation, sentiment, risk, and strategy in one place.',
    },
  ],
  faqEyebrow: 'Research Intelligence FAQ',
  faqTitle: 'Common questions about Research agents',
  faqLead: 'What traders ask about Ivy’s research agents.',
  faqItems: [
    {
      question: 'Which agents are included?',
      answer:
        'Earnings Analyst, Fundamental Valuation, Sentiment & Narrative, Risk Monitor, Competitive & Strategy.',
    },
    {
      question: 'How long does an agent take to run?',
      answer:
        'Most agents return in under a minute. Agent runs for multiple tickers may take a few minutes for a full analysis.',
    },
    {
      question: 'Can I export agent output?',
      answer:
        'Yes. Subject to our Terms of Service, copy sections or the full research results for personal use directly from the agent response.',
    },
    {
      question: 'What sources do the agents use?',
      answer:
        'SEC filings, earnings transcripts, news feeds, and market data linked to the ticker you run.',
    },
    {
      question: 'Can I configure an agent’s behavior?',
      answer:
        'Agents run focused workflows by design. Portfolio risk profile calibration shapes how results relate to your holdings.',
    },
    {
      question: 'Are agents available for non-US tickers?',
      answer: 'US equities are supported today. Additional markets are added over time.',
    },
  ],
  ...sharedFeatureCta,
})
